import { FC, useCallback, useState } from "react";

import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Stack } from "@mui/material";
import { getField, Locales } from "@simplyk/common";

import { FundRow } from "./FundRow";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { CreateFundModal } from "@/components/FundModals/CreateFundModal";
import { EditFundData, EditFundModal } from "@/components/FundModals/EditFundModal";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Add } from "@/icons/outlined";

interface FundDesignationFormContentProps {
  locale: Locales;
  selectedFundIds: string[];
  onSelectedFundIdsChange: (ids: string[]) => void;
  onFundAdded?: () => void;
}

export const FundDesignationFormContent: FC<FundDesignationFormContentProps> = ({
  locale,
  selectedFundIds,
  onSelectedFundIdsChange,
  onFundAdded,
}) => {
  const { t } = useTranslate();
  const utils = trpc.useUtils();

  const { data: organizationFunds } = trpc.getFundsByOrganizationId.useQuery();

  const [editFundData, setEditFundData] = useState<EditFundData | null>(null);
  const [isCreateFundOpen, setIsCreateFundOpen] = useState(false);

  const activeFunds = organizationFunds?.filter((f) => !f.isArchived) ?? [];

  const selectedFunds = selectedFundIds
    .map((id) => activeFunds.find((f) => f.id === id))
    .filter(Boolean) as typeof activeFunds;
  const unselectedFunds = activeFunds.filter((f) => !selectedFundIds.includes(f.id));
  const displayFunds = [...selectedFunds, ...unselectedFunds];

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = selectedFundIds.indexOf(active.id as string);
    const newIndex = selectedFundIds.indexOf(over.id as string);
    onSelectedFundIdsChange(arrayMove(selectedFundIds, oldIndex, newIndex));
  };

  const handleToggleFundVisibility = (fundId: string) => {
    const newIds = selectedFundIds.includes(fundId)
      ? selectedFundIds.filter((id) => id !== fundId)
      : [...selectedFundIds, fundId];
    onSelectedFundIdsChange(newIds);
  };

  const handleEditFund = useCallback((fund: (typeof activeFunds)[number]) => {
    setEditFundData({ id: fund.id, code: fund.code ?? null, fundFields: fund.fundFields });
  }, []);

  const handleFundCreated = useCallback(
    (fundId: string) => {
      onSelectedFundIdsChange([...selectedFundIds, fundId]);
      utils.getFundsByOrganizationId.invalidate();
      onFundAdded?.();
    },
    [selectedFundIds, onSelectedFundIdsChange, utils, onFundAdded]
  );

  return (
    <>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <DashboardTypography variant="body2" vibe="text-neutral-moderate">
            {t("dashboard", "funds.editDesignation.fundsLabel")}
          </DashboardTypography>
          {displayFunds.length === 0 ? (
            <DashboardTypography variant="body2" vibe="text-neutral-quiet">
              {t("dashboard", "funds.editDesignation.noFunds")}
            </DashboardTypography>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={selectedFundIds} strategy={verticalListSortingStrategy}>
                <Stack spacing={1}>
                  {displayFunds.map((fund) => (
                    <FundRow
                      key={fund.id}
                      id={fund.id}
                      name={getField(fund.fundFields, locale)?.name ?? ""}
                      code={fund.code}
                      isVisible={selectedFundIds.includes(fund.id)}
                      formCount={fund.formCount ?? 0}
                      onEdit={() => handleEditFund(fund)}
                      onToggleVisibility={() => handleToggleFundVisibility(fund.id)}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          )}
        </Stack>

        <DashboardButton
          startIcon={<Add />}
          onClick={() => setIsCreateFundOpen(true)}
          variant="tonal"
          vibe="neutral"
          sx={{ alignSelf: "flex-start" }}
        >
          {t("dashboard", "funds.editDesignation.addFund")}
        </DashboardButton>
      </Stack>

      {editFundData && <EditFundModal isOpen onClose={() => setEditFundData(null)} fund={editFundData} />}
      {isCreateFundOpen && (
        <CreateFundModal isOpen onClose={() => setIsCreateFundOpen(false)} onFundCreated={handleFundCreated} />
      )}
    </>
  );
};
